import React, { useCallback, useState } from "react";
import { CheckCircle, Error } from "@mui/icons-material";
import { Alert, Box, Paper, Tooltip, Typography } from "@mui/material";

import { NavigationButton } from "ui";

import { getAllAcceptedExtensions, validateFilePackage } from "fond/fileValidation";
import { LayerStatus } from "fond/hooks/useGetImportLayers";
import { addImport } from "fond/redux/imports";
import { ImportStatus } from "fond/types";
import { getFilesFromEvent } from "fond/utils/files";
import { useAppDispatch, useAppSelector } from "fond/utils/hooks";
import { Dropzone, LinearProgressWithLabel, SvgIcon } from "fond/widgets";

import { getBaseName, groupFilesByLayer, muPowerInputLayers } from "./files";
import UploadModal from "./UploadModal";

interface InputPanelProps {
  isReadOnly?: boolean;
  inputLayers: Map<string, LayerStatus>;
}

const InputPanel: React.FC<InputPanelProps> = ({ isReadOnly, inputLayers }) => {
  const dispatch = useAppDispatch();
  const versionId = useAppSelector((state) => state.project.versionId);
  const [uploadingLayer, setUploadingLayer] = useState<string | null>(null);
  const [bulkErrors, setBulkErrors] = useState<string[]>([]);

  const isAnyUploading = Array.from(inputLayers.values()).some(
    (layer) => layer.status && ["CONVERTING", "IMPORTING", "BUILDING_TILES"].includes(layer.status)
  );

  const handleUpload = (layerKey: string, files: File[]) => {
    const baseName = getBaseName(files[0]?.name || "");
    dispatch(
      addImport({
        versionId,
        layerKey,
        displayName: baseName || layerKey.split("/").pop() || "Layer",
        files,
      })
    );
    setUploadingLayer(null);
  };

  const handleBulkDrop = useCallback(
    (files: File[]) => {
      setBulkErrors([]);
      const { matched, unmatchedNames } = groupFilesByLayer(files);
      const errors: string[] = [];

      if (unmatchedNames.length > 0) {
        errors.push(`Unrecognized layer names: ${unmatchedNames.join(", ")}. Expected: addresses, parcels, streets, poles, boundary.`);
      }

      if (matched.length === 0) {
        errors.push("No files matched any expected layer names.");
        setBulkErrors(errors);
        return;
      }

      for (const group of matched) {
        const { isPackageValid, error } = validateFilePackage(group.files);
        if (!isPackageValid) {
          errors.push(`${group.baseName}: ${error?.message || "Invalid file package"}`);
        } else {
          dispatch(
            addImport({
              versionId,
              layerKey: group.layerKey,
              displayName: group.baseName,
              files: group.files,
            })
          );
        }
      }

      setBulkErrors(errors);
    },
    [dispatch, versionId]
  );

  const getLayerConfig = (layerKey: string) => muPowerInputLayers.find((l) => l.key === layerKey);

  return (
    <>
      {muPowerInputLayers.map((layer) => {
        const layerStatus = inputLayers.get(layer.key);
        const hasLayer = !!layerStatus?.layer;
        const isError = layerStatus?.status === ImportStatus.ERROR;
        const isComplete = hasLayer && layerStatus?.status === ImportStatus.COMPLETE;

        return (
          <NavigationButton
            key={layer.key}
            data-testid={`mu-power-${layer.key.split("/").pop()}-input`}
            icon={<SvgIcon icon={layer.icon} />}
            title={layer.title}
            onClick={() => setUploadingLayer(layer.key)}
            sx={{ m: 0, mt: 1 }}
            disabled={isReadOnly || isAnyUploading}
            checked={isComplete}
            rightElement={
              isError ? (
                <Tooltip title="Import failed">
                  <Error color="error" sx={{ fontSize: 18 }} />
                </Tooltip>
              ) : isComplete ? (
                <CheckCircle color="success" sx={{ fontSize: 18 }} />
              ) : (
                <Tooltip title="Required">
                  <Error sx={{ color: "warning.main", fontSize: 18 }} />
                </Tooltip>
              )
            }
          />
        );
      })}

      <Box sx={{ mt: 1 }}>
        <Dropzone
          data-testid="mu-power-bulk-upload"
          onDropAccepted={handleBulkDrop}
          acceptedFiles={getAllAcceptedExtensions()}
          getFilesFromEvent={getFilesFromEvent}
          message="Drop all layer files here (addresses, parcels, streets, poles, boundary)"
          disabled={isReadOnly || isAnyUploading}
        />
      </Box>

      {bulkErrors.length > 0 && (
        <Alert severity="warning" sx={{ mt: 1 }} onClose={() => setBulkErrors([])}>
          {bulkErrors.map((err) => (
            <Typography key={err} variant="body2">
              {err}
            </Typography>
          ))}
        </Alert>
      )}

      {isAnyUploading && (
        <Paper variant="outlined" sx={{ py: 2, px: 2.75, mt: 2 }}>
          <LinearProgressWithLabel color="primary" variant="indeterminate" title="Importing layer" subtext="Processing..." />
        </Paper>
      )}

      {uploadingLayer && (
        <UploadModal
          layerTitle={getLayerConfig(uploadingLayer)?.title || "Layer"}
          onClose={() => setUploadingLayer(null)}
          onSubmit={(files) => handleUpload(uploadingLayer, files)}
        />
      )}
    </>
  );
};

export default InputPanel;
